/**
 * Cifras según el país de la cuenta (Alex, 12 oct 2026), para los textos que
 * se arman en el motor y se ven en la web:
 *  - España: «0,5 kg · 1.000 g».
 *  - México: «0.5 kg · 1,000 g».
 * El locale lo pone quien llama (la web, con el de la cuenta); sin él, se
 * escribe como en España, que es como se escribía antes.
 *
 * Los miles se agrupan siempre, también con cuatro cifras («1.000 g»): en
 * es-ES, Intl no lo hace por defecto.
 */
export function formatCifra(value: number, locale = 'es-ES', maxDecimals = 3): string {
  const n = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
      useGrouping: true,
    }).format(n === 0 ? 0 : n);
  } catch {
    return String(n);
  }
}

/** Cómo se escribe cada unidad: el litro, siempre «L»; la onza líquida, «fl oz». */
const UNIDAD_VISIBLE: Record<string, string> = { l: 'L', floz: 'fl oz' };

export function unidadVisible(unit: string): string {
  return UNIDAD_VISIBLE[unit] ?? unit;
}

/* ------------------------------------------------------------------ */
/* Fechas (revisión de Alex, 14 oct 2026): siempre «14 sep», nunca      */
/* «14/09/2026» ni «2026-09-28». Con el año solo si no es el de ahora.  */
/* ------------------------------------------------------------------ */

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * «2026-09-14» → «14 sep»; «2025-09-14» → «14 sep 2025» (si el año no es el de
 * `hoy`). Acepta también un ISO con hora (se queda con el día). Lo que no es
 * una fecha se devuelve tal cual.
 */
export function fechaLegible(iso: string, hoy: string = new Date().toISOString().slice(0, 10)): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  if (!m) return String(iso ?? '');
  const mes = MESES_CORTOS[Number(m[2]) - 1];
  if (!mes) return String(iso ?? '');
  const dia = `${Number(m[3])} ${mes}`;
  return m[1] === String(hoy).slice(0, 4) ? dia : `${dia} ${m[1]}`;
}

const esDia = (mes: string, dia: string): boolean => Number(mes) >= 1 && Number(mes) <= 12 && Number(dia) >= 1 && Number(dia) <= 31;

/**
 * Reescribe las fechas de un texto ya armado («vence el 2026-09-28»,
 * «del 14/09/2026») como «28 sep». Para mensajes guardados antes de esta
 * regla (chat de Alexia, borradores del escáner) y para lo que escribe el modelo.
 */
export function fechasLegibles(texto: string, hoy: string = new Date().toISOString().slice(0, 10)): string {
  return String(texto ?? '')
    .replace(/\b(\d{4})-(\d{2})-(\d{2})(?:T[\d:.]+Z?)?(?![\d-])/g, (todo, a: string, me: string, d: string) =>
      esDia(me, d) ? fechaLegible(`${a}-${me}-${d}`, hoy) : todo,
    )
    .replace(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, (todo, d: string, me: string, a: string) =>
      esDia(me, d) ? fechaLegible(`${a}-${me.padStart(2, '0')}-${d.padStart(2, '0')}`, hoy) : todo,
    );
}
